import React, {Suspense} from "react";
import get from "lodash/get";
import {Route, Switch, useParams} from "react-router-dom";
import {useLazyQuery} from "react-apollo";
import {useTranslation, Constants} from "@unibuddy/intl";
import {createFeatureFlaggingProvider, User} from "ub-feature-flagging-react";
import {AppLocalesQueryWithUniPreference} from "shared/Translations/AppLocalesQuery";
import {AVAILABLE_LOCALES} from "shared/Utils/momentLocales";
import {getParameterByName} from "shared/Utils/UrlUtils";
import useErrorReporting from "shared/General/components/ErrorReporting/useErrorReporting";
import WidgetsRouter from "./widgets/navigation/WidgetsRoutes";
import Logout from "./widgets/modules/Logout/Logout";
import {
    UniversityProvider,
    useUniversity,
} from "./widgets/modules/UniversityProvider/UniversityProvider";
import {PageProvider} from "./widgets/modules/PageProvider/PageProvider";
import {getenv} from "./util/getEnv";
import {RecommendationsProvider} from "./widgets/modules/Recommendations/RecommendationsProvider/RecommendationsProvider";
import {useUser} from "./widgets/modules/User/useUser";
import {UnibuddyImpressionsLogo} from "./components/UnibuddyImpressionsLogo/UnibuddyImpressionsLogo";

const PasswordResetRoutes = React.lazy(() => import("shared/PasswordReset/PasswordResetRoutes"));

/**
 * These are the routes currently used as embedded in uni site which contains universitySlug
 */
export const widgetBasePaths = [
    "/embed/:universitySlug", // embedded widget
    "/pwa/:universitySlug", // non embedded widget
    "/:universitySlug/colour", // buddy card
    "/:universitySlug/blog", // blog feed
];

/**
 * A wrapper comoponent which uses universitySlug from route params and sets the correct locale
 */
const LocaleWrapper = ({children}) => {
    const {i18n} = useTranslation();
    const {reportError} = useErrorReporting();
    const {universitySlug} = useParams();
    const [query, {data}] = useLazyQuery(AppLocalesQueryWithUniPreference);

    React.useEffect(() => {
        try {
            query({
                variables: {
                    appName: Constants.APP_NAMES.WIDGET,
                    universitySlug,
                },
            });
        } catch (err) {
            reportError(err);
        }
    }, [query, reportError, universitySlug]);

    React.useEffect(() => {
        const inContextParam = getParameterByName(window.location.href, "incontext");
        if (data && !inContextParam) {
            try {
                const appLocales = get(data, "appLocales.locales", []) || [];
                const preferredLocale =
                    get(data, "university.localePreference.locale", Constants.DEFAULT_LOCALE) ||
                    Constants.DEFAULT_LOCALE;

                const ubLang = getParameterByName(window.location.href, "ub_lang");
                const locale =
                    ubLang && appLocales.find((x) => get(x, "locale") === ubLang)
                        ? ubLang
                        : preferredLocale;

                if (i18n.language !== locale) {
                    i18n.changeLanguage(locale);
                }
                let momentLocale = Constants.DEFAULT_LOCALE.toLowerCase();
                // check if moment lib supports selected locale
                if (AVAILABLE_LOCALES.includes(locale.toLowerCase()))
                    momentLocale = locale.toLowerCase();
                else if (AVAILABLE_LOCALES.includes(locale.substr(0, 2)))
                    momentLocale = locale.substr(0, 2);
                import(`moment/locale/${momentLocale}`);
            } catch (err) {
                reportError(err);
            }
        }
    }, [i18n, data, reportError]);

    return children;
};

// initialise Launch Darkly client
const FeatureFlaggingProvider = createFeatureFlaggingProvider(getenv("UB_LAUNCHDARKLY_CLIENT_ID"));

const LaunchDarklyWrapper = ({children}) => {
    const {universitySlug} = useParams();
    const university = useUniversity();
    const user = useUser();
    const accountRole = user?.accountRole;
    const userId = user?.id;

    // ldUser id = userId if signed in or ub-widget-universitySlug if not signed in
    // this prevents too many MAU in launch darkly if users aren't signed in
    const ldUser = userId
        ? new User(userId, {
              university,
              accountRole,
          })
        : undefined;

    const ldUniversity = university
        ? {id: university.id, slug: university.slug, name: university.name}
        : {
              slug: universitySlug,
          };

    return (
        <FeatureFlaggingProvider user={ldUser} university={ldUniversity}>
            {children}
        </FeatureFlaggingProvider>
    );
};

const Routes = () => (
    <Switch>
        <Suspense fallback={null}>
            <Route
                path={widgetBasePaths}
                render={(routeProps) => {
                    return (
                        <UniversityProvider>
                            <LaunchDarklyWrapper>
                                <LocaleWrapper>
                                    <PageProvider>
                                        <UnibuddyImpressionsLogo routeProps={routeProps} />
                                        <RecommendationsProvider>
                                            <WidgetsRouter {...routeProps} />
                                        </RecommendationsProvider>
                                    </PageProvider>
                                </LocaleWrapper>
                            </LaunchDarklyWrapper>
                        </UniversityProvider>
                    );
                }}
            />
            {/* this route does not have uni slug, hence locale cannot be set to uni preferred unless explicitly provided using data-ub-lang*/}
            <Route path="/auth/logout" component={Logout} />
            {/* this route does not have uni slug, hence locale cannot be set to uni preferred unless explicitly provided using data-ub-lang*/}
            <Route path="/auth/reset" render={(props) => <PasswordResetRoutes {...props} />} />
        </Suspense>
    </Switch>
);

export default Routes;
